import type { IconCode } from "@components/Atoms/Icon/LucideIcon";
import t from "@ext/localization/locale/translate";
import { useViewSurfaceVariant } from "@ext/markdown/elements/view/models/ViewSurfaceVariant";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	useDropdownMenuOpen,
} from "@ui-kit/Dropdown";
import { FloatingTriggerButton } from "@ui-kit/FloatingPanel";
import { GlassToolbarIcon, GlassToolbarTriggerChevron } from "@ui-kit/GlassToolbar";
import { Icon } from "@ui-kit/Icon";
import { Indicator } from "@ui-kit/Indicator";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { forwardRef, type ReactNode } from "react";

export interface ViewButtonProps {
	icon: string;
	children: ReactNode;
	tooltipText: string;
	disabled?: boolean;
	empty?: boolean;
	active?: boolean;
	trigger?: JSX.Element;
}

type ViewButtonTriggerProps = {
	icon: string;
	tooltipText: string;
	disabled: boolean;
	active: boolean;
};

const ViewButtonTrigger = forwardRef<HTMLButtonElement, ViewButtonTriggerProps>(
	({ icon, tooltipText, disabled, active, ...otherProps }, ref) => {
		const variant = useViewSurfaceVariant();
		const isOpen = useDropdownMenuOpen();
		const dataState = isOpen ? "open" : "closed";

		if (variant === "panel")
			return (
				<FloatingTriggerButton
					{...otherProps}
					aria-label={tooltipText}
					className="relative"
					data-state={dataState}
					disabled={disabled}
					ref={ref}
				>
					<Icon className="h-3.5 w-3.5" icon={icon as IconCode} />
					{active && <Indicator className="absolute right-1 top-1 rounded-full bg-status-info" size="sm" />}
				</FloatingTriggerButton>
			);

		return (
			<GlassToolbarTriggerChevron
				{...otherProps}
				active={active}
				aria-label={tooltipText}
				className="disabled:pointer-events-auto disabled:cursor-not-allowed"
				data-state={dataState}
				disabled={disabled}
				ref={ref}
			>
				<GlassToolbarIcon icon={icon as IconCode} />
			</GlassToolbarTriggerChevron>
		);
	},
);

ViewButtonTrigger.displayName = "ViewButtonTrigger";

const ViewButton = ({
	icon,
	disabled = false,
	children,
	tooltipText,
	empty = false,
	active = false,
	trigger,
}: ViewButtonProps) => {
	return (
		<DropdownMenu modal={false}>
			{trigger ? (
				<DropdownMenuTrigger asChild disabled={disabled}>
					{trigger}
				</DropdownMenuTrigger>
			) : (
				<Tooltip>
					<TooltipTrigger asChild>
						<DropdownMenuTrigger asChild disabled={disabled}>
							<ViewButtonTrigger
								active={active}
								disabled={disabled}
								icon={icon}
								tooltipText={tooltipText}
							/>
						</DropdownMenuTrigger>
					</TooltipTrigger>
					<TooltipContent sideOffset={2}>{tooltipText}</TooltipContent>
				</Tooltip>
			)}
			<DropdownMenuContent align="start">
				<DropdownMenuLabel>{tooltipText}</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{empty && <DropdownMenuItem disabled>{t("properties.no-values")}</DropdownMenuItem>}
				{children}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default ViewButton;
